import { Avatar, Box, Divider, Grid, IconButton, Typography } from "@mui/material";
import React, { Fragment, useEffect, useState } from "react";
import CommonCard from "../../common/components/CommonCard";
import theme from "../../theme";
import CommonFormField from "../../common/components/CommonFormField";
import CommonButton from "../../common/components/CommonButton";
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import CommonDialog from "../../common/components/CommonDialog";
import { LoginType, UserTypeId, validateEmail } from "../../common/const";
import CommonSelect from "../../common/components/CommonSelect";
import { useDispatch, useSelector } from "react-redux";
import LoadingSpinner from "../../common/components/LoadingSpinner";
import { setLoading, signUpSuccess } from "../../features/authSlice";
import { changePassword, deleteUser, getCountries, getUser, updateUser } from "../../api/authApi";
import { showNotification } from "../../common/components/Notification/ToastifyNotification";
import { useNavigate } from "react-router-dom";
import { BorderColor, Clear } from "@mui/icons-material";

const PersonalInfo = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { loading, user } = useSelector((state) => state.auth);
    const [errors, setErrors] = useState({});
    const [formData, setFormData] = useState({
        firstname: '',
        lastname: '',
        email: '',
        phone: '',
        birthdate: '',
        address: '',
        state: '',
        city: '',
        zipcode: '',
        country: '',
    });
    const [showOldPassword, setShowOldPassword] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [passwordFormData, setPasswordFormData] = useState({
        oldpassword: '',
        newpassword: '',
        confirmpassword: ''
    });
    const [previewUrl, setPreviewUrl] = useState('');
    const [image, setImage] = useState(null);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [countryArr, setCountryArr] = useState([]);
    const [editPersonalProfile, setEditPersonalProfile] = useState(false);
    const [editPassword, setEditPassword] = useState(false);
    const page = 1;
    const pageSize = 250;

    const validateField = (name, value, allValues = passwordFormData) => {
        switch (name) {
            case 'firstname':
                if (!value) return 'First Name is required';
                return '';
            case 'lastname':
                if (!value) return 'Last Name is required';
                return '';
            case 'phone':
                if (!value) return 'Phone Number is required';
                if (value.includes('+91') ? value.length < 12 : value.length < 11) return 'Please enter valid phone number';
                return '';
            case 'birthdate':
                if (!value) return 'Date of Birth is required';
                return '';
            case 'address':
                if (!value) return 'Address is required';
                return '';
            case 'zipcode':
                if (!value) return 'Zipcode is required';
                return '';
            case 'state':
                if (!value) return 'State is required';
                if (!/^[A-Za-z ]+$/.test(value)) return 'State must contain only alphabetic characters';
                return '';
            case 'city':
                if (!value) return 'City is required';
                if (!/^[A-Za-z ]+$/.test(value)) return 'City must contain only alphabetic characters';
                return '';
            case 'country':
                if (!value) return 'Country is required';
                return '';
            case 'email':
                if (!value) return 'Email is required';
                if (!validateEmail(value)) return 'Please enter a valid email address';
                break;
            case 'oldpassword':
                if (!value) return 'Old Password is required';
                if (value.length < 8) return 'Password must be at least 8 characters';
                if (!/[A-Z]/.test(value)) return 'Password must contain at least one uppercase letter';
                if (!/[a-z]/.test(value)) return 'Password must contain at least one lowercase letter';
                if (!/\d/.test(value)) return 'Password must contain at least one number';
                if (!/[!@#$%^&*(),.?":{}|<>]/.test(value)) return 'Password must contain at least one special character';
                return '';
            case 'newpassword':
                if (!value) return 'New Password is required';
                if (value.length < 8) return 'Password must be at least 8 characters';
                if (!/[A-Z]/.test(value)) return 'Password must contain at least one uppercase letter';
                if (!/[a-z]/.test(value)) return 'Password must contain at least one lowercase letter';
                if (!/\d/.test(value)) return 'Password must contain at least one number';
                if (!/[!@#$%^&*(),.?":{}|<>]/.test(value)) return 'Password must contain at least one special character';
                return '';
            case 'confirmpassword':
                if (!value) return 'Confirm password is required';
                if (value !== allValues.newpassword) return 'Passwords do not match';
                return '';
            default:
                return '';
        }
    };

    const validateForm = () => {
        const newErrors = {};
        Object.keys(formData).forEach(key => {
            const error = validateField(key, formData[key]);
            if (error) newErrors[key] = error;
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
        if (errors[name]) {
            const error = validateField(name, value);
            if (!error) {
                setErrors(prev => ({ ...prev, [name]: undefined }));
            } else {
                setErrors(prev => ({ ...prev, [name]: error }));
            }
        }
    };

    const getCurrentUser = async () => {
        try {
            const response = await getUser(user?.parent_user_id || user?.user_id);
            setFormData({
                firstname: response?.data?.first_name,
                lastname: response?.data?.last_name,
                email: response?.data?.email || user?.email,
                phone: response?.data?.phone_number,
                birthdate: response?.data?.date_of_birth,
                address: response?.data?.address,
                state: response?.data.state,
                city: response?.data.city,
                zipcode: response?.data.zip_code,
                country: response?.data.country_id,
            });
            if (response?.data?.avatar) {
                if (response?.data.login_type === LoginType.EMAIL) {
                    const responseData = await fetch(`${process.env.REACT_APP_BASE_URL}/file?bname=${response?.data.avatar}&download=false`, {
                        headers: {
                            'Authorization': `Bearer ${localStorage.getItem('token')}`
                        }
                    });
                    const blob = await responseData.blob();
                    const imageUrl = URL.createObjectURL(blob);
                    setPreviewUrl(imageUrl || '');
                } else {
                    setPreviewUrl(response?.data.avatar);
                }
            }
            dispatch(setLoading({
                loading: false
            }));
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    }

    useEffect(() => {
        if (user?.user_id) {
            getCurrentUser();
        }
    }, [user?.user_id]);

    const handleProfileSubmit = async (e) => {
        try {
            e.preventDefault();
            if (!validateForm()) return;
            dispatch(setLoading({
                loading: true
            }));
            const formdata = new FormData();
            formdata.append('first_name', formData.firstname);
            formdata.append('last_name', formData.lastname);
            formdata.append('email', formData.email);
            formdata.append('phone_number', formData.phone);
            formdata.append('date_of_birth', new Date(formData.birthdate).toISOString());
            formdata.append('address', formData.address);
            formdata.append('city', formData.city);
            formdata.append('state', formData.state);
            formdata.append('zip_code', formData.zipcode);
            formdata.append('country_id', formData.country);
            if (image) {
                formdata.append('avatar', image);
            } else if (previewUrl === '') {
                formdata.append('avatar', null);
            }
            const response = await updateUser(user?.user_id, formdata);
            if (response?.data?.user_id) {
                showNotification('Personal info updated successful!', 'success');
                dispatch(signUpSuccess({
                    user: {
                        ...user,
                        first_name: formData.firstname,
                        last_name: formData.lastname,
                        avatar: response.data.avatar
                    }
                }));
                getCurrentUser();
                setImage(null);
                setEditPersonalProfile(false);
            }
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            dispatch(setLoading({
                loading: false
            }));
            showNotification(errorMessage, 'error');
        }
    };

    const validatePasswordForm = () => {
        const newErrors = {};
        Object.keys(passwordFormData).forEach(key => {
            const error = validateField(key, passwordFormData[key]);
            if (error) newErrors[key] = error;
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handlePasswordChange = (e) => {
        const { name, value } = e.target;
        setPasswordFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
        if (errors[name]) {
            const error = validateField(name, value);
            if (!error) {
                setErrors(prev => ({ ...prev, [name]: undefined }));
            } else {
                setErrors(prev => ({ ...prev, [name]: error }));
            }
        }
    };

    const handlePasswordSubmit = async (e) => {
        try {
            e.preventDefault();
            if (!validatePasswordForm()) return;
            dispatch(setLoading({
                loading: true
            }));
            const response = await changePassword({
                currentPassword: passwordFormData.oldpassword,
                newPassword: passwordFormData.newpassword,
                confirmPassword: passwordFormData.confirmpassword
            });
            if (response.success) {
                showNotification(response.message, 'success');
                setPasswordFormData({
                    oldpassword: '',
                    newpassword: '',
                    confirmpassword: ''
                });
                dispatch(setLoading({
                    loading: false
                }));
                setEditPassword(false);
            }
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err.response.data?.error;
            dispatch(setLoading({
                loading: false
            }));
            showNotification(errorMessage, 'error');
        }
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreviewUrl(reader.result);
            };
            reader.readAsDataURL(file);
            setImage(file);
        }
    };

    const getCountry = async (page) => {
        const response = await getCountries(page, pageSize);
        setCountryArr(response.countries.map((a) => ({
            label: a.name,
            value: a.country_id
        })));
        dispatch(setLoading({
            loading: false
        }));
    }

    useEffect(() => {
        dispatch(setLoading({
            loading: true
        }));
        getCountry(page);
    }, []);

    const getMaxDate = () => {
        const date = new Date();
        date.setFullYear(date.getFullYear() - 11);
        return date.toISOString().split('T')[0];
    };


    return (
        <Fragment>
            {loading && (
                <LoadingSpinner />
            )}
            <CommonDialog
                open={deleteOpen}
                size="xs"
                content={(
                    <center>
                        <Typography sx={{ fontWeight: 600, fontSize: '22px', mt: 0.5, mb: 1 }}>
                            Are you sure you want to delete profile?
                        </Typography>
                    </center>
                )}
                actionButtons={(
                    <Fragment>
                        <CommonButton
                            label="Yes"
                            size="small"
                            background={theme.palette.secondary.main}
                            color={theme.palette.primary.main}
                            handleClick={async () => {
                                try {
                                    dispatch(setLoading({
                                        loading: true
                                    }));
                                    await deleteUser(user.user_id);
                                    showNotification('User deleted successfully', 'success');
                                    localStorage.clear();
                                    setDeleteOpen(false);
                                    dispatch(setLoading({
                                        loading: false
                                    }));
                                    navigate('/login');
                                } catch (err) {
                                    const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
                                    showNotification(errorMessage, 'error');
                                    dispatch(setLoading({
                                        loading: false
                                    }));
                                }
                            }}
                        />
                        <CommonButton
                            label="No"
                            size="small"
                            background={theme.palette.background.main}
                            border={`1px solid ${theme.palette.background.light}`}
                            handleClick={() => {
                                setDeleteOpen(false);
                            }}
                        />
                    </Fragment>
                )}
            />
            <CommonCard
                borderRadius={1}
                border={`1px solid ${theme.palette.background.secondary}`}
                paddingBottom="0px"
                padding="0px"
                marginTop="0px"
                headerContent={
                    <Box>
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            p={2}
                        >
                            <Box>
                                <Typography fontSize={22} fontWeight={600}>Personal Info</Typography>
                                <Typography fontSize={15} color={theme.palette.background.dark}>Update your personal Details</Typography>
                            </Box>
                            {!editPersonalProfile && (
                                <CommonButton
                                    label="Edit"
                                    handleClick={() => setEditPersonalProfile(true)}
                                    marginBottom="10px"
                                    marginTop="10px"
                                    fontWeight={600}
                                    startIcon={<BorderColor />}
                                    width='12%'
                                />
                            )}
                        </Box>
                        <Divider />
                    </Box>
                }
                content={
                    <Fragment>
                        <Grid container spacing={1} sx={{ padding: '13px', marginBottom: 1 }}>
                            <Grid item xs={12} md={3}>
                                <Box sx={{ position: 'relative', mb: 1, display: 'inline-block' }}>
                                    <Avatar
                                        src={previewUrl}
                                        sx={{
                                            width: 100,
                                            height: 100,
                                            backgroundColor: theme.palette.grey[200],
                                            cursor: 'pointer',
                                        }}
                                        onClick={() => {
                                            if (editPersonalProfile) {
                                                document.getElementById('profile-image-upload').click()
                                            }
                                        }}
                                    >
                                        <PhotoCameraIcon sx={{ fontSize: 60, color: theme.palette.grey[400] }} />
                                    </Avatar>
                                    <input
                                        type="file"
                                        id="profile-image-upload"
                                        accept="image/*"
                                        style={{ display: 'none' }}
                                        onChange={handleFileChange}
                                    />

                                    {previewUrl && editPersonalProfile && (
                                        <IconButton
                                            size="small"
                                            sx={{
                                                position: 'absolute',
                                                top: -8,
                                                right: -8,
                                                backgroundColor: 'error.main',
                                                color: 'white',
                                                width: 24,
                                                height: 24,
                                                '&:hover': {
                                                    backgroundColor: 'error.dark',
                                                },
                                            }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setPreviewUrl('');
                                                setImage(null);
                                            }}
                                        >
                                            <Clear sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    )}
                                </Box>
                            </Grid>
                            <Grid item md={9} />
                            <Grid item xs={12} md={6}>
                                <CommonFormField
                                    label="First Name *"
                                    type="email"
                                    name="firstname"
                                    value={formData.firstname}
                                    onChange={handleInputChange}
                                    placeholder="Enter first name"
                                    error={errors.firstname}
                                    disabled={!editPersonalProfile}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <CommonFormField
                                    label="Last Name *"
                                    type="email"
                                    name="lastname"
                                    value={formData.lastname}
                                    onChange={handleInputChange}
                                    placeholder="Enter last name"
                                    error={errors.lastname}
                                    disabled={!editPersonalProfile}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <CommonFormField
                                    label="Email *"
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    placeholder="Enter email id"
                                    error={errors.email}
                                    disabled
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <CommonFormField
                                    label="Phone Number *"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={(e) => {
                                        setFormData((prev) => ({
                                            ...prev,
                                            phone: e?.phone,
                                        }));
                                        if (errors['phone']) {
                                            const error = validateField('phone', e?.phone);
                                            if (!error) {
                                                setErrors(prev => ({ ...prev, phone: undefined }));
                                            } else {
                                                setErrors(prev => ({ ...prev, phone: error }));
                                            }
                                        }
                                    }}
                                    placeholder="Enter phone number"
                                    error={errors.phone}
                                    isPhone={true}
                                    disabled={!editPersonalProfile}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <CommonFormField
                                    label="Date of Birth *"
                                    type="date"
                                    name="birthdate"
                                    value={formData.birthdate}
                                    onChange={handleInputChange}
                                    placeholder="MM-DD-YYYY"
                                    error={errors.birthdate}
                                    disabled={!editPersonalProfile}
                                    maxDate={getMaxDate()}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <CommonFormField
                                    label="Address *"
                                    type="email"
                                    name="address"
                                    value={formData.address}
                                    onChange={handleInputChange}
                                    placeholder="Enter your address"
                                    error={errors.address}
                                    disabled={!editPersonalProfile}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <CommonFormField
                                    label="Zipcode *"
                                    type="number"
                                    name="zipcode"
                                    value={formData.zipcode}
                                    onChange={handleInputChange}
                                    placeholder="Enter your zipcode"
                                    error={errors.zipcode}
                                    disabled={!editPersonalProfile}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <CommonFormField
                                    label="City *"
                                    type="email"
                                    name="city"
                                    value={formData.city}
                                    onChange={handleInputChange}
                                    placeholder="Enter your city"
                                    error={errors.city}
                                    disabled={!editPersonalProfile}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <CommonFormField
                                    label="State *"
                                    type="email"
                                    name="state"
                                    value={formData.state}
                                    onChange={handleInputChange}
                                    placeholder="Enter your state"
                                    error={errors.state}
                                    disabled={!editPersonalProfile}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <CommonSelect
                                    label="Country *"
                                    name="country"
                                    value={formData.country}
                                    onChange={handleInputChange}
                                    placeholder="Select country"
                                    options={countryArr}
                                    error={errors.country}
                                    disabled={!editPersonalProfile}
                                />
                            </Grid>
                        </Grid>
                        <Divider />
                    </Fragment>
                }
                footerContent={
                    <Box
                        display="flex"
                        justifyContent="flex-end"
                        width="100%"
                        paddingRight="5px"
                    >
                        {editPersonalProfile && (
                            <CommonButton
                                label="Save Changes"
                                handleClick={handleProfileSubmit}
                                marginBottom="10px"
                                marginTop="10px"
                                fontWeight={600}
                                width='14%'
                            />
                        )}
                    </Box>
                }
            />
            <CommonCard
                borderRadius={1}
                border={`1px solid ${theme.palette.background.secondary}`}
                paddingBottom="0px"
                padding="0px"
                marginTop="10px"
                headerContent={
                    <Box>
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            p={2}
                        >
                            <Box p={2}>
                                <Typography fontSize={22} fontWeight={600}>Change Password</Typography>
                                <Typography fontSize={15} color={theme.palette.background.dark}>Keep your account safe</Typography>
                            </Box>
                            {!editPassword && (
                                <CommonButton
                                    label="Edit"
                                    handleClick={() => setEditPassword(true)}
                                    marginBottom="10px"
                                    marginTop="10px"
                                    fontWeight={600}
                                    startIcon={<BorderColor />}
                                    width='12%'
                                />
                            )}
                        </Box>
                        <Divider />
                    </Box>
                }
                content={
                    <Fragment>
                        <Grid container spacing={1} sx={{ padding: '13px', marginBottom: 1 }}>
                            <Grid item xs={12} md={4}>
                                <CommonFormField
                                    label="Old Password *"
                                    type="password"
                                    name="oldpassword"
                                    value={passwordFormData.oldpassword}
                                    onChange={handlePasswordChange}
                                    placeholder="Enter your old password"
                                    error={errors.oldpassword}
                                    showPassword={showOldPassword}
                                    disabled={!editPassword}
                                    onTogglePassword={() => setShowOldPassword(prev => !prev)}
                                    isLetter
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <CommonFormField
                                    label="New Password *"
                                    type="password"
                                    name="newpassword"
                                    value={passwordFormData.newpassword}
                                    onChange={handlePasswordChange}
                                    placeholder="Enter your new password"
                                    error={errors.newpassword}
                                    showPassword={showPassword}
                                    disabled={!editPassword}
                                    onTogglePassword={() => setShowPassword(prev => !prev)}
                                    isLetter
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <CommonFormField
                                    label="Confirm Password *"
                                    type="password"
                                    name="confirmpassword"
                                    value={passwordFormData.confirmpassword}
                                    onChange={handlePasswordChange}
                                    placeholder="Enter your confirm password"
                                    error={errors.confirmpassword}
                                    showPassword={showConfirmPassword}
                                    disabled={!editPassword}
                                    onTogglePassword={() => setShowConfirmPassword(prev => !prev)}
                                    isLetter
                                />
                            </Grid>
                        </Grid>
                        <Divider />
                    </Fragment>
                }
                footerContent={
                    <Box
                        display="flex"
                        justifyContent="flex-end"
                        width="100%"
                        paddingRight="5px"
                    >
                        {editPassword && (
                            <CommonButton
                                label="Save Changes"
                                handleClick={handlePasswordSubmit}
                                marginBottom="10px"
                                marginTop="10px"
                                fontWeight={600}
                                width='14%'
                            />
                        )}
                    </Box>
                }
            />
            {user?.user_type_id === UserTypeId.Student && (
                <CommonCard
                    borderRadius={1}
                    border={`1px solid red`}
                    background='#FFF8F8'
                    paddingBottom="0px"
                    padding="15px"
                    marginTop="10px"
                    content={
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                        >
                            <Box
                                sx={{
                                    width: '100%'
                                }}
                            >
                                <Typography fontSize={25} fontWeight={600} color="red">Delete Profile</Typography>
                                <Typography color={theme.palette.background.dark}>Permanently delete your profile, college wishlist, calendar events, and all</Typography>
                                <Typography color={theme.palette.background.dark}>associated data. This action cannot be undone.</Typography>
                            </Box>
                            <CommonButton
                                label="Delete Profile"
                                color="White"
                                width="13%"
                                background="red"
                                marginBottom="0px"
                                fontWeight={600}
                                handleClick={() => setDeleteOpen(true)}
                            />
                        </Box>
                    }
                />
            )}
        </Fragment>
    )
}

export default PersonalInfo;